/**
 * Poll project until a design finishes generating.
 */
import { fetchProject } from './client.js';

/**
 * @param {string} token
 * @param {number} designId
 * @param {{ intervalMs?: number, maxAttempts?: number, maxConsecutiveErrors?: number }} [options]
 */
export async function pollProjectForDesign(token, designId, options) {
    const intervalMs = (options && options.intervalMs) || 2500;
    const maxAttempts = (options && options.maxAttempts) || 120;
    // Transient network failures (timeout, 5xx, offline blip) must not abort
    // polling — the backend job may still be running. Give up only after
    // several consecutive failures so we do not hang forever on a dead route.
    const maxConsecutiveErrors = (options && options.maxConsecutiveErrors) || 5;
    let consecutiveErrors = 0;

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        await sleep(intervalMs);

        let payload;
        try {
            payload = await fetchProject(token);
        } catch {
            consecutiveErrors += 1;
            if (consecutiveErrors >= maxConsecutiveErrors) {
                throw new Error('Lost connection while checking generation status. Please refresh the page in a moment to see your design.');
            }
            continue;
        }
        consecutiveErrors = 0;

        const designs = payload && payload.project && payload.project.designs
            ? payload.project.designs
            : [];
        const design = designs.find((item) => Number(item.id) === Number(designId));

        if (!design) {
            continue;
        }

        if (design.generation_status === 'ok') {
            return payload;
        }

        if (design.generation_status === 'failed') {
            const fromApi = typeof design.failure_message === 'string' ? design.failure_message.trim() : '';
            const error = new Error(fromApi !== '' ? fromApi : 'Generation failed. Please try a different prompt.');
            error.payload = payload;
            throw error;
        }
    }

    throw new Error(
        'Generation timed out. If this keeps happening, the tote queue worker may be stopped — please try again shortly or refresh the page.',
    );
}

/**
 * @param {number} ms
 */
function sleep(ms) {
    return new Promise((resolve) => {
        window.setTimeout(resolve, ms);
    });
}
